/* eslint-disable no-restricted-globals */
import { FilterTypes } from '../interfaces';
import { Data } from '../../../patterns/commands';
import { StatisticsLaborer } from './Laborer';
import { SortAndFilterLaborer } from '../processing/Laborer';
import SortAndFilterConfig from '../../../config/SortAndFilterConfig';
import {
    getActiveIngredientOptions,
    getBrandOptions,
    getConditionOptions,
    getDrugClassOptions,
    getTreatmentFormOptions,
} from '../../../helpers/filterOptions';
import { RedisTreatment } from '../../../../../lib/redis/types/RedisTreatment';

export interface Statistic {
    type: string;
    key: string | boolean | number | number[];
    statistics: {
        value: number;
    };
}

export interface FilterStatistics {
    total: number;
    statistics: Statistic[];
}

export interface FilterStatisticsResponse {
    review: FilterStatistics;
    price: FilterStatistics;
    conditions: FilterStatistics;
    brand: FilterStatistics;
    treatmentForm: FilterStatistics;
    activeIngredient: FilterStatistics;
    drugClass: FilterStatistics;
}

export type CalculateStatisticsData = (filterTypes: FilterTypes[], data: Data, config: SortAndFilterConfig) => FilterStatisticsResponse;

/**
 * A function exposed by comlink which will be used to
 * calculate statistics for treatment filters off the main thread.
 * @returns FilterStatistics
 */
export const calculateStatisticsData: CalculateStatisticsData = (
    filterTypes: FilterTypes[],
    data: Data,
    config: SortAndFilterConfig
): FilterStatisticsResponse => {
    // Active Ingredient
    const activeIngredientFilters: FilterTypes[] = [
        { type: 'activeIngredient', keys: getActiveIngredientOptions(data as RedisTreatment[], config) },
    ];
    const activeIngredientLaborer = new SortAndFilterLaborer(activeIngredientFilters, []);
    const activeIngredientData = activeIngredientLaborer.processData(data);
    const activeIngredientStatisticsLaborer = new StatisticsLaborer(activeIngredientFilters);
    const activeIngredient = activeIngredientStatisticsLaborer.processData(activeIngredientData) as FilterStatistics;

    // Brand
    const brandFilters: FilterTypes[] = [{ type: 'brand', keys: getBrandOptions(data as RedisTreatment[], config) }];
    const brandLaborer = new SortAndFilterLaborer(brandFilters, []);
    const brandData = brandLaborer.processData(data);
    const brandStatisticsLaborer = new StatisticsLaborer(brandFilters);
    const brand = brandStatisticsLaborer.processData(brandData) as FilterStatistics;

    // Conditions
    const conditionsFilters: FilterTypes[] = [{ type: 'conditions', keys: getConditionOptions(data as RedisTreatment[], config) }];
    const conditionsLaborer = new SortAndFilterLaborer(conditionsFilters, []);
    const conditionsData = conditionsLaborer.processData(data);
    const conditionsStatisticsLaborer = new StatisticsLaborer(conditionsFilters);
    const conditions = conditionsStatisticsLaborer.processData(conditionsData) as FilterStatistics;

    // DrugClass
    const drugClassFilters: FilterTypes[] = [{ type: 'drugClass', keys: getDrugClassOptions(data as RedisTreatment[], config) }];
    const drugClassLaborer = new SortAndFilterLaborer(drugClassFilters, []);
    const drugClassData = drugClassLaborer.processData(data);
    const drugClassStatisticsLaborer = new StatisticsLaborer(drugClassFilters);
    const drugClass = drugClassStatisticsLaborer.processData(drugClassData) as FilterStatistics;

    // Treatment Form
    const treatmentFormFilters: FilterTypes[] = [
        { type: 'treatmentForm', keys: getTreatmentFormOptions(data as RedisTreatment[], config) },
    ];
    const treatmentFormLaborer = new SortAndFilterLaborer(treatmentFormFilters, []);
    const treatmentFormData = treatmentFormLaborer.processData(data);
    const treatmentFormStatisticsLaborer = new StatisticsLaborer(treatmentFormFilters);
    const treatmentForm = treatmentFormStatisticsLaborer.processData(treatmentFormData) as FilterStatistics;

    // Review statistics
    const reviewFilters: FilterTypes[] = [
        {
            keys: [5, 4, 3, 2, 1],
            type: 'review',
        },
    ];
    const reviewLaborer = new SortAndFilterLaborer(reviewFilters, []);
    const reviewData = reviewLaborer.processData(data);
    const reviewStatisiticsLaborer = new StatisticsLaborer(reviewFilters);
    const review = reviewStatisiticsLaborer.processData(reviewData) as FilterStatistics;

    // Price statistics
    const priceFilters: FilterTypes[] = [
        {
            type: 'price',
            keys: config.filters.price?.brackets || [],
        },
    ];
    const priceLaborer = new SortAndFilterLaborer(priceFilters, []);
    const priceData = priceLaborer.processData(data);
    const priceStatisiticsLaborer = new StatisticsLaborer(priceFilters);
    const price = priceStatisiticsLaborer.processData(priceData) as FilterStatistics;

    return { activeIngredient, brand, conditions, drugClass, price, review, treatmentForm };
};

if (typeof self !== 'undefined') {
    const originalOnMessage = self.onmessage;
    self.onmessage = (event) => {
        // Makes sure this only runs for TIP messages.
        if (self.origin.includes('theindependentpharmacy') && event?.data?.filterTypes && event?.data?.data && event?.data?.config) {
            // Handle incoming messages from the main thread
            const { filterTypes, data, config } = event.data;
            const result = calculateStatisticsData(filterTypes, data, config);
            self.postMessage(result);
        } else {
            // Pass the message to other extensions
            // eslint-disable-next-line no-lonely-if
            if (typeof originalOnMessage === 'function') {
                originalOnMessage.call(self, event);
            }
        }
    };
}
